import { inWindow, lonToWindow, projectLinesToViewport, projectPointsToViewport } from "@/utils/viewportHelper";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { GeoJSON, LayerGroup, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import ReactDOMServer from "react-dom/server";

import WarningAmberIcon from "@mui/icons-material/Warning";

import runPlanrouteAnalyser from "@/features/voyagePlanner/runPlanrouteAnalyser";
import { isManualDraft, sameLonLat } from "@/features/voyagePlanner/manualSketch";
import { hasLegWarnings, hasLegInfos, isPlanType, findPlannedStateColor, findRoutePane, ROUTE_PANES, PATHFINDER_COLOR } from "../../utils/routeHelper";
import { useAccessContext } from "../../contexts/AccessContext";
import {
  useIsCalculating,
  useRouteStore,
} from "@/features/route/state/RouteStore.jsx";
import { normalizeRenderedRoute } from "@/features/route/state/RouteAdapters.jsx";
import { selectRouteWeatherLimits, selectVesselMeta } from "@/features/route/state/RouteSelectors.jsx";
import { resolveWeatherLimits } from "@/features/vessel/vesselMeta.js";
import { editWaypointIcon } from "@/features/route/waypointIcons.jsx";
import {
  routeTransitSections,
  transitCardProps,
  transitMarkers,
  transitPointLevels,
} from "@/features/route/transits.js";
import { legCardProps } from "@/features/route/legAlerts.js";
import { CAUTION, DANGER } from "@/theme/tokens";

const makeAlertIcon = (color, editable) =>
  L.divIcon({
    className: editable ? "css-icon wp-edit-marker" : "css-icon",
    html: ReactDOMServer.renderToString(
      <WarningAmberIcon
        style={{
          color,
          fontSize: 24,
          stroke: "white",
          strokeWidth: 1.5,
          paintOrder: "stroke",
          display: "block",
        }}
      />,
    ),
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });

const warningLocationIcon = makeAlertIcon(DANGER, false);
const infoLocationIcon = makeAlertIcon(CAUTION, false);
const editableWarningIcon = makeAlertIcon(DANGER, true);
const editableInfoIcon = makeAlertIcon(CAUTION, true);

const ICON_SPACING_PX = 10;

// The last waypoint has no outgoing leg, so its unchecked-leg flag is meaningless.
const POINT_ICON_INFOS = { includeUncheckedLeg: false };

// Weather and zone runs come from the route's transits, not from the leg.
const alertLevel = (properties, infoOptions) =>
  hasLegWarnings(properties)
    ? "warning"
    : hasLegInfos(properties, infoOptions)
      ? "info"
      : null;

// Midpoint along the drawn line in projected space, so it sits on the line
// for greatcircle arcs and mercator-straight rhumblines alike.
function drawnMidpoint(map, coords) {
  const pts = coords.map(([lng, lat]) => map.project([lat, lng], 0));
  const lens = pts.slice(1).map((p, i) => p.distanceTo(pts[i]));
  let rest = lens.reduce((a, b) => a + b, 0) / 2;
  for (let i = 0; i < lens.length; i++) {
    if (rest <= lens[i] && lens[i] > 0) {
      const a = pts[i];
      const b = pts[i + 1];
      const t = rest / lens[i];
      return map.unproject(L.point(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t), 0);
    }
    rest -= lens[i];
  }
  return map.unproject(pts[pts.length - 1], 0);
}

// astar's segment warnings describe the leg after a waypoint, so they sit mid-leg.
function legAlertAnchors(lineGeojson, norm, map) {
  const wrapped = projectLinesToViewport(lineGeojson, norm, true, false);
  const anchors = (wrapped?.features ?? []).flatMap((f, legIdx) => {
    const { wp1, wp2 } = f?.properties?.analyse ?? {};
    const coords = f?.geometry?.coordinates;
    const alert = alertLevel(wp1?.properties);
    if (!alert || f.geometry?.type !== "LineString" || !coords || coords.length < 2) return [];
    return [{ legIdx, wp1, wp2, alert, latlng: drawnMidpoint(map, coords) }];
  });
  const placed = [];
  return ["warning", "info"].flatMap((alert) =>
    anchors.filter((a) => {
      if (a.alert !== alert) return false;
      const p = map.latLngToLayerPoint(a.latlng);
      if (placed.some((q) => q.distanceTo(p) < ICON_SPACING_PX)) return false;
      placed.push(p);
      return true;
    }),
  );
}

// Thinned after the leg markers; red before orange.
function placeTransitMarkers(markers, legAnchors, map, norm) {
  const placed = legAnchors.map((a) => map.latLngToLayerPoint(a.latlng));
  const order = (m) => (m.level === "warning" ? 0 : 1);
  return [...markers]
    .sort((a, b) => order(a) - order(b))
    .flatMap((m) => {
      const latlng = L.latLng(m.lonlat[1], lonToWindow(m.lonlat[0], norm));
      if (!inWindow(latlng.lng, norm)) return [];
      const p = map.latLngToLayerPoint(latlng);
      if (placed.some((q) => q.distanceTo(p) < ICON_SPACING_PX)) return [];
      placed.push(p);
      return [{ ...m, latlng }];
    });
}

// Thin big icons (alert triangles, edit rings) per view so dense waypoint
// runs don't stack; the rest fall back to small dots. Priority: warning >
// info > plain editable ring. Re-evaluated on every move/zoom via viewportTick.
function pickIconFeatures(features, map, isEditableWaypoint, alertOf) {
  const shown = new Set();
  const placed = [];
  const place = (feature) => {
    if (feature.geometry?.type !== "Point") return;
    const [lng, lat] = feature.geometry.coordinates;
    const p = map.latLngToLayerPoint([lat, lng]);
    if (placed.some((q) => q.distanceTo(p) < ICON_SPACING_PX)) return;
    placed.push(p);
    shown.add(feature);
  };
  const tier = (f) => {
    const alert = alertOf(f);
    return alert === "warning" ? 0 : alert === "info" ? 1 : isEditableWaypoint(f) ? 2 : 3;
  };
  for (let t = 0; t < 3; t++) {
    features.filter((f) => tier(f) === t).forEach(place);
  }
  return shown;
}

export default function RoutePoints({
  vesselRoutes,
  mapBounds,
  calculatorDepartureTime,
  calculatorBasespeed,
  setEtaResponse,
  speedProfileData,
  consumptionProfileData,
  setRouteConsumption,
  setPathfinderResponse,
  setEtaError,
  norm,
  viewportTick,
  openWaypointCard,
  openAlertCard,
  plannerVesselSettings,
  cwViaAreasGeojson,
  applyRnwSafety,
  applyEca,
  avoidHra,
  avoidJwla,
  avoidCaa,
  plannerDraftRouteId = null,
}) {
  const { accessContext, setAccessContext } = useAccessContext();
  const { state: routeState, replaceRoute, setIsCalculating } = useRouteStore();
  const isCalculating = useIsCalculating();

  const isCalculatingRef = useRef(isCalculating);
  useEffect(() => {
    isCalculatingRef.current = isCalculating;
  }, [isCalculating]);

  const map = useMap();
  for (const [name, z] of Object.entries(ROUTE_PANES)) {
    if (!map.getPane(name)) map.createPane(name);
    map.getPane(name).style.zIndex = z;
  }

  const [draggingMarker, setDraggingMarker] = useState(null);
  const [draggingFeature, setDraggingFeature] = useState(null);
  // The edited draft's points + identity/settings for the recalc effect.
  const [recalcTarget, setRecalcTarget] = useState(null);

  // Trigger the ETA calculation only when an edit set a recalc target.
  useEffect(() => {
    if (
      recalcTarget?.points?.features?.length > 0 &&
      calculatorDepartureTime &&
      calculatorBasespeed &&
      speedProfileData &&
      consumptionProfileData
    ) {
      // Only the planner's own draft feeds the Result step and uses its fields.
      const isPlannerDraft =
        plannerDraftRouteId != null && recalcTarget.routeId === plannerDraftRouteId;
      if (isPlannerDraft) setEtaError(null);
      runPlanrouteAnalyser({
        accessContext,
        setAccessContext,
        voyagePlannerPoints: recalcTarget.points,
        calculatorDepartureTime,
        calculatorBasespeed,
        plannerVesselSettings,
        speedProfileData,
        consumptionProfileData,
        ...resolveWeatherLimits(
          isPlannerDraft ? plannerVesselSettings : null,
          recalcTarget.vesselMeta,
        ),
        cwViaAreasGeojson,
        applyRnwSafety,
        applyEca,
        avoidHra,
        avoidJwla,
        avoidCaa,
        setEtaResponse,
        setRouteConsumption,
        setPathfinderResponse,
        setEtaError,
        replaceRoute,
        setIsCalculating,
        draftRouteId: recalcTarget.routeId,
        previousJsonRequest: recalcTarget.jsonRequest,
        vesselName: recalcTarget.vesselName,
        ownTiming: !isPlannerDraft,
      });
      // One-shot: without this, the departure auto-bump (usePlannerFormState
      // pulls a past departure up to "now" every minute) changes a dep and
      // re-runs the analyser every minute with a drifting departure.
      setRecalcTarget(null);
    }
  }, [
    recalcTarget,
    calculatorDepartureTime,
    calculatorBasespeed,
    speedProfileData,
    consumptionProfileData,
    plannerVesselSettings,
    cwViaAreasGeojson,
    applyRnwSafety,
    applyEca,
    avoidHra,
    avoidJwla,
    avoidCaa,
    plannerDraftRouteId,
  ]);

  // Edit target for the analyser; sketches are geometry only and never recalc.
  const requestRecalc = (source, updated) => {
    if (source?.sketch) return;
    setRecalcTarget({
      points: updated.points.geojson,
      routeId: updated?.points?.routeId ?? null,
      jsonRequest: source?.jsonRequest ?? null,
      vesselName: updated?.points?.vesselName ?? "Pathfinder",
      vesselMeta: selectVesselMeta(routeState, updated?.points?.vesselId),
    });
  };

  useEffect(() => {
    const onMouseMove = (e) => {
      if (draggingMarker) {
        draggingMarker.setLatLng(e.latlng);
      }
    };

    const onMouseUp = () => {
      if (draggingMarker && draggingFeature) {
        const latlng = draggingMarker.getLatLng();
        const updatedCoordinates = [latlng.lng, latlng.lat];
        // Released within click tolerance of the original position (e.g. a
        // jittery click to open the waypoint card): restore the marker and
        // skip the writeback + recalc entirely.
        const origCoords = draggingFeature?.geometry?.coordinates;
        if (Array.isArray(origCoords) && origCoords.length === 2) {
          const movedPx = map
            .latLngToContainerPoint(latlng)
            .distanceTo(
              map.latLngToContainerPoint([origCoords[1], origCoords[0]]),
            );
          if (movedPx <= 6) {
            draggingMarker.setLatLng([origCoords[1], origCoords[0]]);
            setDraggingMarker(null);
            setDraggingFeature(null);
            map.dragging.enable();
            return;
          }
        }
        // The dragged feature came out of projectPointsToViewport, which
        // clones source features. Reference equality (feature === draggingFeature)
        // therefore never matches — the source feature is a different object.
        // Match by stable identity instead: properties.number is unique per
        // waypoint and survives projection.
        const dragNumber = draggingFeature?.properties?.number;
        // Waypoint numbers restart per route — scope to the dragged route.
        const dragRouteId = draggingFeature?.properties?.routeId ?? null;
        const updatedVesselRoutes = vesselRoutes.map((route) => {
          if (
            dragRouteId != null &&
            (route?.points?.routeId ?? route?.lines?.routeId) !== dragRouteId
          ) {
            return route;
          }
          const updatedPoints = route.points.geojson.features.map((feature) => {
            if (
              dragNumber != null &&
              feature?.properties?.number === dragNumber
            ) {
              return {
                ...feature,
                geometry: {
                  ...feature.geometry,
                  coordinates: updatedCoordinates,
                },
                properties: { ...feature.properties, type: "original_point" },
              };
            }
            return feature;
          });

          return {
            ...route,
            points: {
              ...route.points,
              geojson: { ...route.points.geojson, features: updatedPoints },
            },
          };
        });

        // Trigger the recalc useEffect for the edited route only.
        const draggedSource = vesselRoutes.find(
          (route) =>
            (route?.points?.routeId ?? route?.lines?.routeId) === dragRouteId,
        ) ?? vesselRoutes[0];
        const draggedUpdated = updatedVesselRoutes.find(
          (route) =>
            (route?.points?.routeId ?? route?.lines?.routeId) === dragRouteId,
        ) ?? updatedVesselRoutes[0];
        requestRecalc(draggedSource, draggedUpdated);

        // Reflect the drag in the route store. Per-route dispatch using
        // the route's own id — RoutePoints can be mounted for the
        // Pathfinder candidate (routeId -1) or for saved plan/reported
        // routes (DB ids), and we don't want a drag in one instance to
        // overwrite another.
        updatedVesselRoutes.forEach((route) => {
          const targetRouteId =
            route?.points?.routeId ?? route?.lines?.routeId ?? null;
          if (targetRouteId == null) return;
          const fragment = normalizeRenderedRoute(targetRouteId, route);
          replaceRoute(targetRouteId, fragment);
        });

        setDraggingMarker(null);
        setDraggingFeature(null);
        map.dragging.enable();
      }
    };

    map.on("mousemove", onMouseMove);
    map.on("mouseup", onMouseUp);

    return () => {
      map.off("mousemove", onMouseMove);
      map.off("mouseup", onMouseUp);
    };
  }, [map, draggingMarker, draggingFeature, vesselRoutes]);

  // Delete a point and connect the remaining points; true if dispatched.
  const deleteWaypoint = (featureToDelete) => {
    if (isCalculatingRef.current) return false;
    // __idx/coords are only meaningful within the feature's own route.
    const deleteRouteId = featureToDelete?.properties?.routeId ?? null;
    const updatedVesselRoutes = vesselRoutes.map((route) => {
      if (
        deleteRouteId != null &&
        (route?.points?.routeId ?? route?.lines?.routeId) !== deleteRouteId
      ) {
        return route;
      }
      const origFeatures = route.points.geojson.features;
      // Prefer stable original index passed via projection
      const origIndex =
        featureToDelete?.properties?.__idx ??
        origFeatures.findIndex(
          (f) =>
            f.geometry?.coordinates?.toString() ===
            featureToDelete.geometry?.coordinates?.toString(),
        );
      if (origIndex < 0) return route; // nothing to do if we can't resolve

      // Remove the point at origIndex; a sketch gets no recalc, so renumber here.
      const remainingPoints = origFeatures.filter((_, idx) => idx !== origIndex);
      const updatedPoints = route?.sketch
        ? remainingPoints.map((f, idx) => ({
            ...f,
            properties: { ...f.properties, number: idx + 1 },
          }))
        : remainingPoints;

      return {
        ...route,
        points: {
          ...route.points,
          geojson: { ...route.points.geojson, features: updatedPoints },
        },
      };
    });

    const deletedSource = vesselRoutes.find(
      (route) =>
        (route?.points?.routeId ?? route?.lines?.routeId) === deleteRouteId,
    ) ?? vesselRoutes[0];
    const deletedUpdated = updatedVesselRoutes.find(
      (route) =>
        (route?.points?.routeId ?? route?.lines?.routeId) === deleteRouteId,
    ) ?? updatedVesselRoutes[0];
    requestRecalc(deletedSource, deletedUpdated);

    updatedVesselRoutes.forEach((route) => {
      const targetRouteId =
        route?.points?.routeId ?? route?.lines?.routeId ?? null;
      if (targetRouteId == null) return;
      const fragment = normalizeRenderedRoute(targetRouteId, route);
      replaceRoute(targetRouteId, fragment);
    });
    return true;
  };

  // A point's legtype describes its outgoing leg.
  const changeLegtype = (feature, route, legtype) => {
    if (isCalculatingRef.current) return;
    const features = route?.points?.geojson?.features ?? [];
    const idx = feature?.properties?.__idx ?? features.indexOf(feature);
    if (idx < 0 || idx >= features.length - 1) return;
    const updatedPoints = features.map((f, i) =>
      i === idx
        ? { ...f, properties: { ...f.properties, legtype, legType: legtype } }
        : f,
    );
    const updated = {
      ...route,
      points: {
        ...route.points,
        geojson: { ...route.points.geojson, features: updatedPoints },
      },
    };
    const routeId = route?.points?.routeId ?? null;
    if (routeId == null) return;
    requestRecalc(route, updated);
    replaceRoute(routeId, normalizeRenderedRoute(routeId, updated));
  };

  // The waypoint card outlives this render (and possibly this instance), so
  // its actions go through refs holding the latest handlers and route list.
  const deleteWaypointRef = useRef(() => false);
  const changeLegtypeRef = useRef(() => {});
  const vesselRoutesRef = useRef(vesselRoutes);
  vesselRoutesRef.current = vesselRoutes;
  // Leaflet keeps the onEachFeature click handlers of the first render.
  const routeStateRef = useRef(routeState);
  routeStateRef.current = routeState;
  useEffect(() => {
    deleteWaypointRef.current = deleteWaypoint;
    changeLegtypeRef.current = changeLegtype;
    return () => {
      deleteWaypointRef.current = () => false;
      changeLegtypeRef.current = () => {};
    };
  });

  // Resolved by position at action time: indices shift on recalc, the clicked feature is wrapped,
  // and a recalc that keeps the positions does not remount the layer, so its features are stale.
  const liveTargetOf = (route, feature) => {
    const coords = feature.geometry?.coordinates;
    const liveRoute = (vesselRoutesRef.current ?? []).find(
      (r) => r?.points?.routeId === route?.points?.routeId,
    );
    const liveFeature = (liveRoute?.points?.geojson?.features ?? []).find(
      (f) =>
        f?.properties?.type === feature.properties?.type &&
        sameLonLat(f?.geometry?.coordinates, coords),
    );
    return { liveRoute, liveFeature };
  };

  const openCardFor = (feature, route) => {
    const coords = feature.geometry?.coordinates;
    if (!coords) return;
    const liveTarget = () => liveTargetOf(route, feature);
    const { liveRoute = route, liveFeature = feature } = liveTarget();
    openWaypointCard(
      liveFeature,
      { lat: coords[1], lng: coords[0] },
      liveRoute.points.routeType,
      liveRoute.points.vesselName,
      liveRoute.points.state,
      {
        manual: isManualDraft(route),
        sketch: route?.sketch === true,
        onDelete: () => {
          const { liveFeature } = liveTarget();
          if (liveFeature) deleteWaypointRef.current(liveFeature);
        },
        onLegtypeChange: (legtype) => {
          const { liveRoute, liveFeature } = liveTarget();
          if (liveFeature) changeLegtypeRef.current(liveFeature, liveRoute, legtype);
        },
      },
      selectRouteWeatherLimits(routeStateRef.current, liveRoute),
    );
  };

  return vesselRoutes === null ? null : (
    <LayerGroup>
      {vesselRoutes.map((route, idx) => {
        const routePoints = route.points;
        // Import previews are read-only on the map (no drag/delete markers).
        const editable = route?.source !== "import";
        const pointCount = routePoints.geojson?.features?.length ?? 0;
        // A sketch's start/finish are the planner's endpoint markers.
        const isSketchEndpoint = (feature) =>
          route?.sketch &&
          (feature.properties?.__idx === 0 ||
            feature.properties?.__idx === pointCount - 1);
        const isEditableWaypoint = (feature) =>
          editable &&
          route.points.routeType === "Pathfinder" &&
          feature.properties?.type === "original_point" &&
          !isSketchEndpoint(feature);
        // Build a key from original (unwrapped) coordinates to force GeoJSON remount when points change
        const originalCoordsKey = JSON.stringify(
          (routePoints.geojson?.features || []).map(
            (f) => f?.geometry?.coordinates,
          ),
        );
        const filteredData = routePoints.geojson;
        const filteredDataWrapped = projectPointsToViewport(
          filteredData,
          norm,
          true,
          true,
        );
        const alertsShown =
          isPlanType(routePoints.routeType) || routePoints.routeType === "Pathfinder";
        const legAnchors = alertsShown
          ? legAlertAnchors(route.lines?.geojson, norm, map)
          : [];
        const transitSections = alertsShown ? routeTransitSections(route) : [];
        const transitLevels = transitPointLevels(transitSections);
        const shownTransitMarkers = placeTransitMarkers(
          transitMarkers(transitSections),
          legAnchors,
          map,
          norm,
        );
        const openTransit = (marker) =>
          openAlertCard?.(
            { kind: "transit", ...transitCardProps(marker.sections, routePoints.geojson.features) },
            marker.latlng,
            routePoints.routeType,
            routePoints.vesselName,
            routePoints.state,
            routePoints.routeId,
            route.summary,
          );
        // Only the last waypoint keeps its alert: it has no leg to carry it.
        const alertOf = (feature) =>
          alertsShown && feature.properties?.__idx === pointCount - 1
            ? alertLevel(feature.properties, POINT_ICON_INFOS)
            : null;
        // Waypoints take the colour of an adjacent marked leg or the transit section they lie in.
        const colorAlertOf = (feature) => {
          if (!alertsShown) return null;
          const idx = feature.properties?.__idx;
          const levels = [
            alertOf(feature),
            transitLevels.get(idx) ?? null,
            idx < pointCount - 1 ? alertLevel(feature.properties) : null,
            idx > 0 ? alertLevel(routePoints.geojson.features[idx - 1]?.properties) : null,
          ];
          return levels.includes("warning") ? "warning" : levels.includes("info") ? "info" : null;
        };
        const hasPoints = !!filteredDataWrapped.features?.length;
        if (!hasPoints && !legAnchors.length && !shownTransitMarkers.length) return null;
        const iconFeatures = pickIconFeatures(
          filteredDataWrapped.features ?? [],
          map,
          isEditableWaypoint,
          alertOf,
        );
        const legPane = findRoutePane("point", routePoints.routeType, routePoints.state);
        return (
          <React.Fragment key={`RoutePoints-${routePoints.routeId}-${idx}`}>
          {legAnchors.map(({ legIdx, wp1, wp2, alert, latlng }) => (
            <Marker
              key={`LegAlert-${routePoints.routeId}-${legIdx}-${viewportTick}`}
              position={latlng}
              icon={alert === "warning" ? warningLocationIcon : infoLocationIcon}
              pane={legPane}
              eventHandlers={{
                click: () =>
                  openAlertCard?.(
                    { kind: "leg", ...legCardProps(wp1, wp2) },
                    latlng,
                    routePoints.routeType,
                    routePoints.vesselName,
                    routePoints.state,
                    routePoints.routeId,
                    route.summary,
                  ),
              }}
            />
          ))}
          {shownTransitMarkers.map((m) => (
            <Marker
              key={`Transit-${routePoints.routeId}-${m.lonlat.join(",")}-${viewportTick}`}
              position={m.latlng}
              icon={m.level === "warning" ? warningLocationIcon : infoLocationIcon}
              pane={legPane}
              eventHandlers={{ click: () => openTransit(m) }}
            />
          ))}
          {hasPoints && (
          <GeoJSON
            key={`RoutePoints-${routePoints.routeId}-${routePoints.state}-${route?.sketch ? "sketch" : ""}-${mapBounds}-${viewportTick}-${originalCoordsKey}`}
            data={filteredDataWrapped}
            style={{ fillOpacity: 1 }}
            pointToLayer={(feature, latlon) => {
              const pointPane = findRoutePane(
                "point",
                route.points.routeType,
                route.points.state,
              );
              let markerProps = {
                radius: 4,
                weight: 1,
                opacity: 1,
                fillOpacity: 1,
                fillColor: "blue",
                color: "blue",
                pane: pointPane,
              };

              if (
                route.points.routeType === "reported" &&
                feature.properties.albis_reports !== undefined &&
                feature.properties.albis_reports !== null
              ) {
                markerProps.radius = 7;
                markerProps.weight = 3;
                markerProps.color = "white";
              } else if (alertsShown) {
                const alert = colorAlertOf(feature);
                const color =
                  alert === "warning"
                    ? DANGER
                    : alert === "info"
                      ? CAUTION
                      : isPlanType(route.points.routeType)
                        ? findPlannedStateColor(route.points.state)
                        : PATHFINDER_COLOR;
                markerProps.fillColor = color;
                markerProps.color = color;
              }

              let marker;
              if (isEditableWaypoint(feature) && iconFeatures.has(feature)) {
                const alert = alertOf(feature);
                const icon =
                  alert === "warning"
                    ? editableWarningIcon
                    : alert === "info"
                      ? editableInfoIcon
                      : editWaypointIcon;
                marker = L.marker(latlon, {
                  icon,
                  pane: pointPane,
                });

                let dragCheck = null;

                let isDragging = false;
                let startPos = null;

                marker.on("mousedown", (e) => {
                  // Ignore drags while a recalc is in flight to prevent
                  // racing requests from clobbering each other.
                  if (isCalculatingRef.current) return;
                  startPos = e.containerPoint;
                  isDragging = false;

                  map.dragging.disable();

                  dragCheck = (moveEvent) => {
                    if (!startPos) return;
                    const dx = moveEvent.containerPoint.x - startPos.x;
                    const dy = moveEvent.containerPoint.y - startPos.y;

                    // true Euclidean distance squared
                    const distSq = dx * dx + dy * dy;

                    // (5 px)^2 = 25
                    if (distSq > 25 && !isDragging) {
                      isDragging = true;
                      map.getContainer().classList.add("wp-dragging");
                      setDraggingMarker(marker);
                      setDraggingFeature(feature);
                    }
                  };

                  map.on("mousemove", dragCheck);

                  // Tear down on release anywhere on the map — a mouseup a
                  // few pixels off the icon skips the marker's own mouseup,
                  // which used to leave map dragging disabled with a stale
                  // dragCheck attached, so the next map pan was misread as a
                  // waypoint drag (waypoint teleports + unwanted recalc).
                  map.once("mouseup", () => {
                    if (dragCheck) {
                      map.off("mousemove", dragCheck);
                      dragCheck = null;
                    }
                    map.getContainer().classList.remove("wp-dragging");
                    map.dragging.enable();
                    startPos = null;
                  });
                });

                marker.on("contextmenu", (e) => {
                  // A right-click still fires marker "mousedown" first, which
                  // disabled map dragging and registered the dragCheck mousemove
                  // listener; tear that interaction down before opening the card.
                  L.DomEvent.stop(e);
                  if (dragCheck) {
                    map.off("mousemove", dragCheck);
                    dragCheck = null;
                  }
                  startPos = null;
                  isDragging = false;
                  map.dragging.enable();
                  openCardFor(feature, route);
                });
              } else if (
                route.points.routeType === "Pathfinder" &&
                feature.properties.type !== "antimeridian_point"
              ) {
                marker = L.circleMarker(latlon, markerProps);
              } else if (
                isPlanType(route.points.routeType) &&
                iconFeatures.has(feature)
              ) {
                marker = L.marker(latlon, {
                  icon: alertOf(feature) === "warning" ? warningLocationIcon : infoLocationIcon,
                  pane: pointPane,
                });
              } else {
                marker = L.circleMarker(latlon, markerProps);
              }

              return marker;
            }}
            onEachFeature={(feature, layer) => {
              layer.on("click", () => {
                if (isEditableWaypoint(feature)) {
                  openCardFor(feature, route);
                  return;
                }
                const coords = feature.geometry?.coordinates;
                if (coords) {
                  const { liveRoute = route, liveFeature = feature } = liveTargetOf(route, feature);
                  openWaypointCard(
                    liveFeature,
                    { lat: coords[1], lng: coords[0] },
                    liveRoute.points.routeType,
                    liveRoute.points.vesselName,
                    liveRoute.points.state,
                    null,
                    selectRouteWeatherLimits(routeStateRef.current, liveRoute),
                  );
                }
              });
            }}
          />
          )}
          </React.Fragment>
        );
      })}
    </LayerGroup>
  );
}
